import { Formik } from 'formik';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import AddIcon from '@material-ui/icons/Add';
import { CloudUpload, Close } from '@material-ui/icons';
import { formatISO, parseISO } from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Collapse } from '@material-ui/core';
import styled from 'styled-components';
import cloneDeep from 'lodash.clonedeep';
import {
  Button,
  DialogModal,
  DrawerWindow,
  LoadMask,
  Page,
  showErrorToast,
} from '../../../../components';
import FilterIndicator from '../../../../components/FilterIndicator';
import LoadIcon from '../../../../components/LoadIcon';
import { OrderStatusEnum } from '../../../../constants';
import { ITEMS_PER_PAGE } from '../../../../constants/Paginate';
import { OrderRoutePaths, RoutePaths } from '../../../../constants/RoutePaths';
import { USER_ROLES } from '../../../../constants/UsersRoles';
import {
  fieldNames,
  filtersInitialState,
  luftFieldNames,
} from '../../../../constants/order';
import { HttpService, OrderService } from '../../../../services';
import { PreOrderService } from '../../../../services/PreOrderService';
import { getNumberOfAppliedFilters } from '../../../../utils';
import { useDidUpdateEffect } from '../../../../utils/hooks/did-update-effect';
import Filters from './filters';
import OrderDetail from './orderDetail';
import OrdersTable from './ordersTable';
import Export from './Export';
import { ERPLoading, ExportButtonERP } from './styles';

const orderByInitialState = {
  field: undefined,
  orderType: undefined,
};
const searchInitialState = {
  type: undefined,
  term: '',
};

export const updateQueryStringInOrderList = (
  history,
  {
    filters, search, orderBy, skip, take,
  },
) => {
  history.push(
    `?${HttpService.serializeQueryString({
      companyList: Object.keys(filters.companyList).map((key) => filters.companyList[key] ? key : null,
      ),
      createdAtFrom: filters.createdAt.from
        ? formatISO(filters.createdAt.from, { representation: 'date' })
        : null,
      createdAtTo: filters.createdAt.to
        ? formatISO(filters.createdAt.to, { representation: 'date' })
        : null,
      deliveryDateFrom: filters.deliveryDate.from
        ? formatISO(filters.deliveryDate.from, { representation: 'date' })
        : null,
      deliveryDateTo: filters.deliveryDate.to
        ? formatISO(filters.deliveryDate.to, { representation: 'date' })
        : null,
      agentList: Object.keys(filters.agentList).map((key) => filters.agentList[key] ? key : null,
      ),
      authorList: Object.keys(filters.authorList).map((key) => filters.authorList[key] ? key : null,
      ),
      statusList: Object.keys(filters.statusList).map((key) => filters.statusList[key] ? key : null,
      ),
      productTypeList: Object.keys(filters.productTypeList).map((key) => filters.productTypeList[key] ? key : null,
      ),
      ERPFileStatus: Object.keys(filters.ERPFileStatus).map((key) => filters.ERPFileStatus[key] ? key : null,
      ),
      promotionalCicleList: Object.keys(filters.promotionalCicleList).map(
        (key) => (filters.promotionalCicleList[key] ? key : null),
      ),
      materialList: Object.keys(filters.materialList).map((key) => filters.materialList[key] ? key : null,
      ),
      // productList: Object.keys(filters.productList)
      //   .map((key) => filters.productList[key] ? key : null),
      searchTerm: search.term,
      take,
      skip,
      ...(orderBy.field && orderBy.orderType
        ? {
          orderByField: orderBy.field,
          orderByType: orderBy.orderType,
        }
        : {}),
    })}`,
  );
};

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlertWrapper = styled(Alert)`
  display: flex;
  align-items: center;

  > .MuiAlert-message {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 14px;
    font-weight: 400;

    > span {
      font-size: 16px;
      font-weight: 600;
    }
  }
`;

const OrderListLayout = ({
  userData,
  title,
  loading,
  agentList,
  authorList,
  numberPendingERP,
  promotionalCicleList,
  materialList,
  productList,
  orderList,
  companyList,
  orderDetailData,
  tagBusinessUnityList,
  onRequestPendingERP,
  onRequestOrderDetail,
  onRequestStatusERP,
  onRequestAgentList,
  onRequestAuthorList,
  onRequestPromotionalCicleList,
  onRequestMaterialList,
  onRequestOrderList,
  onRequestCompanyList,
  onExportFilteredData,
  onExportSelectedData,
  companyDialogList,
  handleOpenCompanyDialog,
  handleOutputCompanyDialog,
  loadingDialog,
  handleConfirmApproveDialog,
  handleConfirmRefuseDialog,
  handleConfirmCancelDialog,
  handleConfirmMultipleApproveDialog,
  onResetApprovalState,
  loadingOrderDialog,
  orderOnApprovalFinished,
  loadingOrderOnMultipleApprovalDialog,
  orderTotalOnMultipleApproval,
  orderOnMultipleApproval,
  orderOnMultipleApprovalFinished,
}) => {
  const history = useHistory();
  const [erpAlert, setErpAlert] = useState(
    numberPendingERP && numberPendingERP !== 0,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [appliedFilters, setAppliedFilters] = useState(filtersInitialState);
  const [countAppliedFilters, setCountAppliedFilters] = useState(0);
  const [openDrawer, setDrawerState] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderBy, setOrderBy] = useState(orderByInitialState);
  const [search, setSearch] = useState(searchInitialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showFilterIndicator, setShowFilterIndicator] = useState(false);
  const [isApproveDialog, setIsApproveDialog] = useState({
    id: null,
    open: false,
  });
  const [isRefuseDialog, setIsRefuseDialog] = useState({
    id: null,
    open: false,
  });
  const [isCancelDialog, setIsCancelDialog] = useState({
    ids: [],
    open: false,
  });
  const [isMultipleApproval, setIsMultipleApproval] = useState({
    rejectedOrders: null,
    orders: null,
    open: false,
  });
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [preOrderIdsList, setPreOrderIdsList] = useState([]);
  const [preOrderIdsLoading, setPreOrderIdsLoading] = useState(false);
  const [preOrderIdsHasMore, setPreOrderIdsHasMore] = useState(false);
  const [preOrderIdsSearch, setPreOrderIdsSearch] = useState('');
  const [orderIdsList, setOrderIdsList] = useState([]);
  const [orderIdsLoading, setOrderIdsLoading] = useState(false);
  const [orderIdsHasMore, setOrderIdsHasMore] = useState(false);
  const [orderIdsSearch, setOrderIdsSearch] = useState('');
  const exportPayloadRef = useRef(null);

  const handleExportPayloadChange = useCallback((payload) => {
    exportPayloadRef.current = payload;
  }, []);

  const handleExportConfirm = useCallback(async () => {
    const payload = exportPayloadRef.current;
    if (!payload) return;
    setExportLoading(true);
    try {
      await OrderService.downloadExportData(
        payload,
        'relatorio_exportacao.csv',
      );
      setExportModalOpen(false);
    } catch (e) {
      console.error('Error exporting data', e);
    } finally {
      setExportLoading(false);
    }
  }, []);

  const fetchPreOrderIds = useCallback(async ({ lastId, search } = {}) => {
    setPreOrderIdsLoading(true);
    try {
      const response = await PreOrderService.fetchPreOrderIds({
        take: 10,
        lastId,
        search: search || undefined,
      });
      const items = response?.data?.items ?? [];
      const hasMore = response?.data?.hasMore ?? false;
      if (lastId) {
        setPreOrderIdsList((prev) => [...prev, ...items]);
      } else {
        setPreOrderIdsList(items);
      }
      setPreOrderIdsHasMore(hasMore);
    } catch (e) {
      console.error('Error fetching pre-order IDs', e);
    } finally {
      setPreOrderIdsLoading(false);
    }
  }, []);

  const handlePreOrderIdsLoadMore = useCallback(() => {
    if (preOrderIdsLoading || !preOrderIdsHasMore || preOrderIdsList.length === 0) return;
    const lastId = preOrderIdsList[preOrderIdsList.length - 1];
    fetchPreOrderIds({ lastId, search: preOrderIdsSearch });
  }, [preOrderIdsLoading, preOrderIdsHasMore, preOrderIdsList, preOrderIdsSearch, fetchPreOrderIds]);

  const handlePreOrderIdsSearch = useCallback((search) => {
    setPreOrderIdsSearch(search);
    fetchPreOrderIds({ search });
  }, [fetchPreOrderIds]);

  const fetchOrderIds = useCallback(async ({ lastId, search } = {}) => {
    setOrderIdsLoading(true);
    try {
      const response = await OrderService.fetchOrderIds({
        take: 10,
        lastId,
        search: search || undefined,
      });
      const items = response?.data?.data?.items ?? [];
      const hasMore = response?.data?.data?.hasMore ?? false;
      if (lastId) {
        setOrderIdsList((prev) => [...prev, ...items]);
      } else {
        setOrderIdsList(items);
      }
      setOrderIdsHasMore(hasMore);
    } catch (e) {
      console.error('Error fetching order IDs', e);
    } finally {
      setOrderIdsLoading(false);
    }
  }, []);

  const handleOrderIdsLoadMore = useCallback(() => {
    if (orderIdsLoading || !orderIdsHasMore || orderIdsList.length === 0) return;
    const lastId = orderIdsList[orderIdsList.length - 1];
    fetchOrderIds({ lastId, search: orderIdsSearch });
  }, [orderIdsLoading, orderIdsHasMore, orderIdsList, orderIdsSearch, fetchOrderIds]);

  const handleOrderIdsSearch = useCallback((search) => {
    setOrderIdsSearch(search);
    fetchOrderIds({ search });
  }, [fetchOrderIds]);

  const [nfeNumbersList, setNfeNumbersList] = useState([]);
  const [nfeNumbersLoading, setNfeNumbersLoading] = useState(false);

  const fetchNfeNumbers = useCallback(async () => {
    setNfeNumbersLoading(true);
    try {
      const response = await OrderService.fetchNfeNumbers();
      setNfeNumbersList(response?.data?.items ?? []);
    } catch (e) {
      console.error('Error fetching NFe numbers', e);
    } finally {
      setNfeNumbersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (exportModalOpen) {
      setPreOrderIdsSearch('');
      fetchPreOrderIds();
      setOrderIdsSearch('');
      fetchOrderIds();
      fetchNfeNumbers();
    }
  }, [exportModalOpen, fetchPreOrderIds, fetchOrderIds, fetchNfeNumbers]);

  const { search: locationSearch } = useLocation();
  const location = useLocation();

  const _buildOrderRequestFromQueryString = () => {
    const queryObject = HttpService.unserializeQueryString(locationSearch);
    const skip = queryObject.skip || 0;
    const take = queryObject.take || ITEMS_PER_PAGE;

    if (locationSearch === '') {
      return {
        filters: {
          ...filtersInitialState,
        },
        take,
        skip,
        orderBy: {
          ...orderByInitialState,
        },
        search: {
          ...searchInitialState,
        },
      };
    }

    return {
      filters: {
        companyList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.companyList,
          null,
          { ...appliedFilters.companyList },
        ),
        createdAt: {
          from: queryObject.createdAtFrom
            ? parseISO(queryObject.createdAtFrom)
            : null,
          to: queryObject.createdAtTo
            ? parseISO(queryObject.createdAtTo)
            : null,
        },
        deliveryDate: {
          from: queryObject.deliveryDateFrom
            ? parseISO(queryObject.deliveryDateFrom)
            : null,
          to: queryObject.deliveryDateTo
            ? parseISO(queryObject.deliveryDateTo)
            : null,
        },
        agentList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.agentList,
          null,
          { ...appliedFilters.agentList },
        ),
        authorList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.authorList,
          null,
          { ...appliedFilters.authorList },
        ),
        statusList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.statusList,
          Number,
          { ...appliedFilters.statusList },
        ),
        ERPFileStatus: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.ERPFileStatus,
          null,
          { ...appliedFilters.ERPFileStatus },
        ),
        promotionalCicleList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.promotionalCicleList,
          null,
          { ...appliedFilters.promotionalCicleList },
        ),
        materialList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.materialList,
          null,
          { ...appliedFilters.materialList },
        ),
        productTypeList: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.productTypeList,
          Number,
          { ...appliedFilters.productTypeList },
        ),
        // productList: HttpService.unserializeQueryStringCheckboxObject(
        //   queryObject.productList, null, { ...appliedFilters.productList }),
      },
      take,
      skip,
      orderBy: {
        field: queryObject.orderByField,
        orderType: Number(queryObject.orderByType),
      },
      search: {
        term: queryObject.searchTerm,
        type: queryObject.searchType,
      },
    };
  };

  const _onReloadDataTable = (filters, orderBy, search) => {
    const requestParams = {
      filters,
      take: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      orderBy,
      search,
    };

    setCountAppliedFilters(
      getNumberOfAppliedFilters(filtersInitialState, filters),
    );
    onRequestOrderList(requestParams);
  };

  useEffect(() => {
    const { filters, orderBy, search } = _buildOrderRequestFromQueryString();

    if (
      location.state
      && location.state.materialId
      && location.state.statusList
    ) {
      filtersInitialState[fieldNames.MATERIAL] = {};
      filters[fieldNames.MATERIAL] = location.state.materialId;
      filters[fieldNames.STATUS] = location.state.statusList;
      filters[fieldNames.STATUS_ERP] = location.state.ERPFileStatus;
    }
    if (location.state && location.state.agentId) {
      filtersInitialState[fieldNames.AGENT] = {};
      filters[fieldNames.AGENT] = location.state.agentId;
    }
    setAppliedFilters(filters);
    setOrderBy(orderBy);
    setSearch(search);

    _onReloadDataTable(filters, orderBy, search);
    _onShowFilterIndicator(filters);
    setIsInitialized(true);
  }, []);

  useDidUpdateEffect(() => {
    if (isInitialized) return;
    _onReloadDataTable(appliedFilters, orderBy, search);
    _onShowFilterIndicator(appliedFilters);
  }, [
    currentPage,
    itemsPerPage,
    appliedFilters,
    isInitialized,
    orderBy,
    search,
  ]);

  const _onShowFilterIndicator = (filters) => {
    setShowFilterIndicator(
      !isEqual(filtersInitialState, filters) || search.term !== '',
    );
  };

  useEffect(() => {
    if (numberPendingERP) {
      setIsLoadingTable(true);
      onRequestPendingERP().then(setIsLoadingTable(false)).catch();
    }
  }, []);

  const _onPageChange = (event, newPage) => {
    setIsInitialized(false);
    setCurrentPage(newPage);
  };

  const _onChangeItemsPerPage = (event, newItemsPerPage) => {
    setIsInitialized(false);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const _onFiltersChange = ({ orderBy, ...newFilters }) => {
    setIsInitialized(false);
    setSearch(searchInitialState);
    setAppliedFilters(cloneDeep(newFilters));
    setCurrentPage(1);
  };

  const _onClearFilter = (handleReset) => {
    setIsInitialized(false);
    if (handleReset) handleReset();
    setAppliedFilters({ ...filtersInitialState });
    setCurrentPage(1);
  };

  const _onChangeDataOrder = (field, orderType) => {
    setIsInitialized(false);
    setOrderBy({
      field,
      orderType,
    });
  };

  const _onSearch = (value) => {
    setIsInitialized(false);
    setAppliedFilters({
      ...filtersInitialState,
    });
    setSearch(value);
    setCurrentPage(1);
  };

  const onMultipleApproveSelectedData = (orders) => {
    const rejectedOrders = [];

    orders.forEach((order) => {
      if (order.data.status !== OrderStatusEnum.ORDER_PENDING_APPROVAL) {
        rejectedOrders.push(order.data.id);
      }
    });

    if (rejectedOrders.length > 0) {
      setIsMultipleApproval({
        rejectedOrders,
        orders,
        open: true,
      });
    } else {
      setIsMultipleApproval({
        rejectedOrders: null,
        orders,
        open: true,
      });
      handleConfirmMultipleApproveDialog(orders);
    }
  };

  const _onClearSearch = (handleReset) => {
    if (handleReset) handleReset();
    setSearch(searchInitialState);
    _onClearFilter();
  };

  const _onRequestOrderDetail = (data) => {
    setDrawerState(true);
    onRequestOrderDetail(data.id);
    setOrderData([{ data }]);
    setOrderNumber(data.orderNumber);
  };

  const _onCloseDrawer = () => {
    setDrawerState(false);
  };

  const _onCloseDialog = () => {
    setIsApproveDialog({ id: null, open: false });
    setIsRefuseDialog({ id: null, open: false });
    setIsCancelDialog({ ids: [], open: false });
    setIsMultipleApproval({ rejectedOrders: null, orders: null, open: false });
    if (onResetApprovalState) {
      onResetApprovalState();
    }
    if (orderOnApprovalFinished || orderOnMultipleApprovalFinished) {
      setDrawerState(false);
      setTimeout(() => {
        onRequestOrderList({
          filters: appliedFilters,
          take: itemsPerPage,
          skip: (currentPage - 1) * itemsPerPage,
          orderBy,
          search,
        });
      }, 300);
    }
  };

  const _onOpenUrls = (nfes) => {
    nfes.map((item) => (item.url ? window.open(item.url) : null));
  };

  const GenerateAllERPFilesErrors = async () => {
    setIsLoadingTable(true);
    try {
      await OrderService.fetchGenerateAllPendingERPErrors();
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsLoadingTable(false);
    }
  };

  const onRequestTryAgain = async () => {
    setIsLoadingTable(true);

    if (orderData[0]) {
      try {
        await OrderService.fetchTryAgainGenerateERPFile(orderData[0].data.id);
        setDrawerState(false);
      } catch (error) {
        showErrorToast(error.message);
      }
    }

    setIsLoadingTable(false);
  };

  const rolesPermitted = userData.userRole !== USER_ROLES.MASTER;

  return (
    <Page
      userData={userData}
      alertComponent={(
        <>
          {/* REMINDER: Validar o bloqueio de pedidos */}
          {/* <Collapse in={orderBlockedAlert}>
            <AlertWrapper
              severity="info"
              action={
                <ExportButtonERP
                  onClick={() => setOrderBlockedAlert(false)}
                >
                  <Close />
                </ExportButtonERP>
              }
            >
              <span>Criação de novos pedidos bloqueada</span>
              A criação de novos pedidos está bloqueada até o dia DD/MM/AA, por conta do ciclo Y.
            </AlertWrapper>
          </Collapse> */}
          <Collapse in={erpAlert}>
            <AlertWrapper
              severity="warning"
              action={(
                <ActionWrapper>
                  <ExportButtonERP
                    size="small"
                    variant="text"
                    onClick={GenerateAllERPFilesErrors}
                  >
                    Gerar arquivo ERP
                  </ExportButtonERP>
                  <ExportButtonERP onClick={() => setErpAlert(false)}>
                    <Close />
                  </ExportButtonERP>
                </ActionWrapper>
              )}
              onClose={() => setErpAlert(false)}
            >
              <span>Arquivos ERP pendentes</span>
              {`${
                numberPendingERP > 1 ? 'Existem' : 'Existe'
              } ${numberPendingERP} ${
                numberPendingERP > 1 ? 'pedidos' : 'pedido'
              } com geração ERP pendente.`}
            </AlertWrapper>
          </Collapse>
        </>
      )}
      disableGuttersFromHeader
      title={title}
      renderRight={() => (
        <div style={{ display: 'flex', gap: '10px' }}>
          {rolesPermitted && (
            <Button
              size="large"
              color="secondary"
              variant="outlined"
              onClick={() => setExportModalOpen(true)}
              endIcon={<CloudUpload />}
              style={{ fontWeight: '500' }}
            >
              Exportar
            </Button>
          )}
          {rolesPermitted && (
            <Button
              size="large"
              color="secondary"
              variant="contained"
              onClick={() => {
                history.push(`${RoutePaths.ORDER}${OrderRoutePaths.NEW}`);
              }}
              endIcon={<AddIcon />}
              style={{ fontWeight: '500' }}
            >
              Novo pedido
            </Button>
          )}
        </div>
      )}
      companyDialogList={companyDialogList}
      loadingDialog={loadingDialog}
      handleOpenCompanyDialog={handleOpenCompanyDialog}
      handleOutputCompanyDialog={(companyId) => {
        handleOutputCompanyDialog(companyId, () => {
          _onClearSearch();
        });
      }}
      openDrawer={openDrawer}
    >
      {/* {numberPendingERP && numberPendingERP !== 0 ? (
        <Alert
          onClose={() => {}}
          action={
            <ExportButtonERP
              size="small"
              variant="text"
              onClick={GenerateAllERPFilesErrors}
            >
              Gerar arquivo ERP
            </ExportButtonERP>
          }
        >
          {`Existe${
            numberPendingERP > 1 ? "Existem" : "Existe"
          } ${numberPendingERP} ${
            numberPendingERP > 1 ? "pedidos" : "pedido"
          } com geração ERP pendente.`}
        </Alert>
      ) : null} */}

      <Formik
        initialValues={{
          [luftFieldNames.COMPANY]: filtersInitialState[luftFieldNames.COMPANY],
          [fieldNames.CREATE_DATE]: filtersInitialState[fieldNames.CREATE_DATE],
          [fieldNames.DELIVERY_DATE]:
            filtersInitialState[fieldNames.DELIVERY_DATE],
          [fieldNames.AGENT]: filtersInitialState[fieldNames.AGENT],
          [fieldNames.AUTHOR]: filtersInitialState[fieldNames.AUTHOR],
          [fieldNames.STATUS]: filtersInitialState[fieldNames.STATUS],
          [fieldNames.PRODUCT_TYPE]:
            filtersInitialState[fieldNames.PRODUCT_TYPE],
          [fieldNames.STATUS_ERP]: filtersInitialState[fieldNames.STATUS_ERP],
          [fieldNames.PROMOTIONAL_CICLE]:
            filtersInitialState[fieldNames.PROMOTIONAL_CICLE],
          [fieldNames.MATERIAL]: filtersInitialState[fieldNames.MATERIAL],
        }}
        onSubmit={_onFiltersChange}
        enableReinitialize
      >
        {({ handleReset }) => (
          <>
            <Filters
              userData={userData}
              agentList={agentList}
              authorList={authorList}
              promotionalCicleList={promotionalCicleList}
              materialList={materialList}
              productList={productList}
              companyList={companyList}
              onRequestStatusERP={onRequestStatusERP}
              onRequestAgentList={onRequestAgentList}
              onRequestAuthorList={onRequestAuthorList}
              tagBusinessUnityList={tagBusinessUnityList}
              onRequestPromotionalCicleList={onRequestPromotionalCicleList}
              onRequestMaterialList={onRequestMaterialList}
              onRequestCompanyList={onRequestCompanyList}
              onSearch={_onSearch}
              listCountResults={orderList.list.length}
              appliedFilters={appliedFilters}
            />
            {showFilterIndicator ? (
              <FilterIndicator
                userData={userData}
                onExport={() => onExportFilteredData(appliedFilters, search)}
                onClear={() => _onClearFilter(handleReset)}
                onClearSearch={() => _onClearSearch(handleReset)}
                appliedFilters={countAppliedFilters}
                searchTerm={search.term}
                hideExport
                listCountResults={orderList.list.length}
              />
            ) : null}
            {isLoadingTable ? (
              <ERPLoading>
                <LoadIcon color="#394E98" />
                <span>
                  Aguarde enquanto estamos
                  <br />
                  gerando os arquivos ERP...
                </span>
              </ERPLoading>
            ) : (
              <OrdersTable
                userData={userData}
                data={orderList.list}
                loading={orderList.loading}
                currentPage={currentPage}
                totalItems={orderList.total}
                itemsPerPage={itemsPerPage}
                onPageChange={_onPageChange}
                onChangeItemsPerPage={_onChangeItemsPerPage}
                onExportSelectedData={onExportSelectedData}
                onClearFilter={() => _onClearFilter(handleReset)}
                onApproveSelectedData={onMultipleApproveSelectedData}
                onCancelSelectedData={(selectedRows) => {
                  const orderIds = selectedRows.map((row) => row.data.id);
                  setIsCancelDialog({
                    ids: orderIds,
                    open: true,
                  });
                }}
                onChangeDataOrder={_onChangeDataOrder}
                dataOrder={orderBy}
                onRequestOrderDetail={_onRequestOrderDetail}
                multiselect={
                  userData.userRole !== USER_ROLES.MASTER
                  || (userData.userRole === USER_ROLES.MASTER
                    && !!userData.companyName)
                }
              />
            )}
          </>
        )}
      </Formik>
      <DrawerWindow
        openDrawer={
          orderDetailData.data.receiver !== undefined
          && !orderDetailData.loading
          && openDrawer
        }
        onToggle={_onCloseDrawer}
      >
        <OrderDetail
          userData={userData}
          isLoadingTable={isLoadingTable}
          orderNumber={orderNumber}
          orderData={orderData}
          orderDetailData={orderDetailData}
          onCloseDrawer={_onCloseDrawer}
          onRequestTryAgain={onRequestTryAgain}
          onExportSelectedData={onExportSelectedData}
          openApproveOrderDialog={(id, open) => setIsApproveDialog({ id, open })}
          openRefuseOrderDialog={(id, open) => setIsRefuseDialog({ id, open })}
          openCancelOrderDialog={(ids, open) => setIsCancelDialog({ ids, open })}
          handleOpenUrls={_onOpenUrls}
        />
      </DrawerWindow>

      <DialogModal
        title={orderOnApprovalFinished ? 'Pedido aprovado' : 'Aprovar pedido'}
        description={
          orderOnApprovalFinished
            ? ''
            : 'Tem certeza que deseja aprovar este pedido?'
        }
        cancelButtonText={orderOnApprovalFinished ? 'Fechar' : 'Voltar'}
        hideConfirmButton={orderOnApprovalFinished}
        confirmButtonText="Aprovar pedido"
        handleCloseDialog={_onCloseDialog}
        loadingDialog={loadingOrderDialog}
        handleConfirmDialog={() => {
          handleConfirmApproveDialog(isApproveDialog.id);
        }}
        isOpenDialog={isApproveDialog.open}
      />

      <DialogModal
        title={orderOnApprovalFinished ? 'Pedido recusado' : 'Recusar pedido'}
        description={
          orderOnApprovalFinished
            ? ''
            : 'Tem certeza que deseja recusar este pedido?'
        }
        cancelButtonText={orderOnApprovalFinished ? 'Fechar' : 'Voltar'}
        hideConfirmButton={orderOnApprovalFinished}
        confirmButtonText="Recusar pedido"
        handleCloseDialog={_onCloseDialog}
        loadingDialog={loadingOrderDialog}
        handleConfirmDialog={() => {
          handleConfirmRefuseDialog(isRefuseDialog.id);
        }}
        isOpenDialog={isRefuseDialog.open}
      />

      <DialogModal
        title={
          isMultipleApproval.rejectedOrders && !orderOnMultipleApprovalFinished
            ? 'Aprovar pedidos'
            : 'Pedidos aprovados'
        }
        description={
          isMultipleApproval.rejectedOrders && !orderOnMultipleApprovalFinished
            ? `Os seguintes pedidos não poderão ser aprovados porque não estão com aprovação pendente: ${isMultipleApproval.rejectedOrders.join(
              ', ',
            )}`
            : `Pronto, ${orderOnMultipleApproval} de ${orderTotalOnMultipleApproval} pedidos selecionados foram aprovados.`
        }
        cancelButtonText={
          isMultipleApproval.rejectedOrders && !orderOnMultipleApprovalFinished
            ? 'Cancelar'
            : 'Fechar'
        }
        hideConfirmButton={
          !isMultipleApproval.rejectedOrders || orderOnMultipleApprovalFinished
        }
        confirmButtonText="Prosseguir assim mesmo"
        handleCloseDialog={_onCloseDialog}
        loadingDialog={loadingOrderOnMultipleApprovalDialog}
        handleConfirmDialog={() => {
          handleConfirmMultipleApproveDialog(isMultipleApproval.orders);
        }}
        isOpenDialog={isMultipleApproval.open}
      />

      <DialogModal
        title={
          orderOnApprovalFinished
            ? isCancelDialog.ids.length > 1
              ? 'Pedidos cancelados'
              : 'Pedido cancelado'
            : isCancelDialog.ids.length > 1
              ? 'Cancelar pedidos'
              : 'Cancelar pedido'
        }
        description={
          orderOnApprovalFinished
            ? ''
            : isCancelDialog.ids.length > 1
              ? 'Tem certeza que deseja cancelar estes pedidos?'
              : 'Tem certeza que deseja cancelar este pedido?'
        }
        cancelButtonText={orderOnApprovalFinished ? 'Fechar' : 'Voltar'}
        hideConfirmButton={orderOnApprovalFinished}
        confirmButtonText={
          isCancelDialog.ids.length > 1
            ? 'Cancelar pedidos'
            : 'Cancelar pedido'
        }
        handleCloseDialog={_onCloseDialog}
        loadingDialog={loadingOrderDialog}
        handleConfirmDialog={() => {
          handleConfirmCancelDialog(isCancelDialog.ids, true);
        }}
        isOpenDialog={isCancelDialog.open}
      />

      <DialogModal
        title="Exportar Relatórios"
        size="lg"
        isOpenDialog={exportModalOpen}
        handleCloseDialog={() => setExportModalOpen(false)}
        handleConfirmDialog={handleExportConfirm}
        confirmButtonText="Exportar"
        loadingDialog={exportLoading}
        hasBorder
        useModalSpacing
      >
        <Export
          onExportPayloadChange={handleExportPayloadChange}
          authorList={authorList}
          orderIds={orderIdsList}
          orderIdsLoading={orderIdsLoading}
          orderIdsHasMore={orderIdsHasMore}
          onOrderIdsLoadMore={handleOrderIdsLoadMore}
          onOrderIdsSearch={handleOrderIdsSearch}
          nfeNumbers={nfeNumbersList}
          nfeNumbersLoading={nfeNumbersLoading}
          preOrderIds={preOrderIdsList}
          preOrderIdsLoading={preOrderIdsLoading}
          preOrderIdsHasMore={preOrderIdsHasMore}
          onPreOrderIdsLoadMore={handlePreOrderIdsLoadMore}
          onPreOrderIdsSearch={handlePreOrderIdsSearch}
        />
      </DialogModal>

      {loading ? <LoadMask /> : null}
    </Page>
  );
};

OrderListLayout.propTypes = {
  userData: PropTypes.object,
  loading: PropTypes.bool,
  title: PropTypes.string,
  agentList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }),
  materialList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  orderList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  productList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  promotionalCicleList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }).isRequired,
  companyList: PropTypes.shape({
    list: PropTypes.any,
    loading: PropTypes.any,
    total: PropTypes.any,
  }),
  onExportFilteredData: PropTypes.func.isRequired,
  onExportSelectedData: PropTypes.func.isRequired,
  onRequestAgentList: PropTypes.func,
  onRequestMaterialList: PropTypes.func.isRequired,
  onRequestOrderList: PropTypes.func.isRequired,
  onRequestProductList: PropTypes.func.isRequired,
  onRequestPromotionalCicleList: PropTypes.func.isRequired,
  onRequestCompanyList: PropTypes.func,
  companyDialogList: PropTypes.object,
  loadingDialog: PropTypes.bool,
  handleOpenCompanyDialog: PropTypes.func,
  handleOutputCompanyDialog: PropTypes.func,
  loadingOrderDialog: PropTypes.bool,
  orderOnApprovalFinished: PropTypes.bool,
};

OrderListLayout.defaultProps = {
  userData: {},
  loading: false,
  title: '',
  onRequestCompanyList: () => {},
  agentList: {
    list: [],
    loading: false,
    total: 0,
  },
  companyList: {
    list: [],
    loading: false,
    total: 0,
  },
  loadingOrderDialog: false,
  orderOnApprovalFinished: false,
  companyDialogList: {},
  loadingDialog: false,
  handleOpenCompanyDialog: () => {},
  handleOutputCompanyDialog: () => {},
  onRequestAgentList: () => {},
};

export default OrderListLayout;
